import { LayoutGrid } from '@blueprinthq/joy'
import {
  Flex,
  GridItem,
  Heading,
  HStack,
  useRadio,
  useRadioGroup
} from '@chakra-ui/react'
import { ClinicianOverride } from '@components'
import { getClientStatusLabel } from '@utilities'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { find } from 'lodash'
import React, { useEffect } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { usePermissions } from '../../../../hooks'

// bp stands for breakpoint not blueprint
const bpdesktop = 'md'
const bptablet = 'sm'
const bpmobile = 'base'

export default function PatientsListHeader(props) {
  const user = useStoreState(state => state.auth.user)
  const { hasPermission } = usePermissions()

  const showDropdown = hasPermission('read:clinic:activeclients:*')

  return (
    <LayoutGrid autoRows gap={5}>
      <GridItem colStart={1} colEnd={2} rowStart={1}>
        <Flex height="100%" alignItems="center">
          <Heading size="xl" textTransform="capitalize">{`Clients`}</Heading>
        </Flex>
      </GridItem>

      <GridItem
        colStart={{
          [bpmobile]: 1,
          [bptablet]: 2,
          [bpdesktop]: 7
        }}
        colEnd={{
          [bpmobile]: 5,
          [bptablet]: 9,
          [bpdesktop]: 13
        }}
        rowStart={{
          [bpmobile]: 2,
          [bptablet]: 1,
          [bpdesktop]: 1
        }}
      >
        <Flex
          height="100%"
          width="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          {showDropdown && (
            <ClinicianOverride
              user={user}
              canViewOrgDropdown={hasPermission('update:org:clinics:*')}
              onChange={props.onClinicianOverrideChange}
              allowAllClinicians={props.canViewAllClinicians}
              selections={{
                clinic: props.currentClinic,
                clinicianId: props.currentClinicianId
              }}
            />
          )}
        </Flex>
      </GridItem>

      <GridItem
        colStart={1}
        colEnd={{
          [bpmobile]: 5,
          [bptablet]: 9,
          [bpdesktop]: 13
        }}
        rowStart={{
          [bpmobile]: 3,
          [bptablet]: 2,
          [bpdesktop]: 2
        }}
      >
        <Flex height="100%" justifyContent="flex-start" alignItems="center">
          <PatientTypeSelector counts={props.counts} />
        </Flex>
      </GridItem>
    </LayoutGrid>
  )
}

function PatientListOption(props) {
  const { getInputProps, getCheckboxProps } = useRadio(props)

  const input = getInputProps()
  const checkbox = getCheckboxProps()

  return (
    // Flex props used to enable horizontal scrolling
    <Flex as="label" flexGrow={0} flexShrink={0} flexBasis="auto">
      <input {...input} />
      <Flex
        {...checkbox}
        height="40px"
        cursor="pointer"
        p="9px 16px"
        bg="#FFFFFF"
        borderRadius="20px"
        fontWeight="bold"
        fontSize="16px"
        _hover={{
          bg: `#28282808`
        }}
        _checked={{
          bg: '#282828',
          color: '#FFFFFF'
        }}
        justifyContent="center"
        alignItems="center"
      >
        {props.children}
      </Flex>
    </Flex>
  )
}

function PatientTypeSelector(props) {
  const history = useHistory()

  const match = useRouteMatch('/patients/:patientType')
  const { patientTypeOptions } = useStoreState(state => state.patientList)
  const { setPatientTypeOption, setPage } = useStoreActions(
    actions => actions.patientList
  )

  useEffect(() => {
    setPatientTypeOption(match.params.patientType)
    // If we dont reset the page count on bucket change, server side pagination might return an empty
    // client list erroneously
    setPage(1)
  }, [match.params.patientType])

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'patientTypes',
    defaultValue: match.params.patientType,
    onChange: e => {
      setPatientTypeOption(e)
      history.push(`/patients/${e}`)
    }
  })
  const group = getRootProps()

  function generateOptionCopy(option) {
    const optionCount =
      props && props.counts && find(props.counts, { status: option.value })

    const label = getClientStatusLabel(option.status)
    return `${label}${
      !option.hideCount ? ` (${optionCount ? optionCount.count : 0})` : ''
    }`
  }

  return (
    // Flex and overflow props used to enable horizontal scrolling
    <HStack {...group} pt="8px" flexWrap="nowrap" overflowX="auto">
      {patientTypeOptions.map(o => {
        const radio = getRadioProps({ value: o.value })
        return (
          <PatientListOption key={o.value} {...radio}>
            {generateOptionCopy(o)}
          </PatientListOption>
        )
      })}
    </HStack>
  )
}
